import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import {
  CheckBox,
  DateField,
  DefaultButton,
  DefaultCard,
  FormValueTypes,
  PaddedContent,
  useFormHook,
} from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { civilStatus } from 'src/data/civilStatus.json';

import { GeneralCatalogItem, Quotation } from 'data-layer-cr/dist/models';
import {
  setRequestStatus,
  useNetworkObserver,
} from 'data-layer-cr/dist/network';

import { format } from 'date-fns';

// Redux
import {
  resetCedulaFiles,
  setDataFields,
  setIdentificationBase64,
  setIdentificationOptionalBase64,
  setLegalBeneficiaries,
  setNewDataFields,
  updateEmissionByPage,
} from 'src/store/emissionPolicy/actions';
import {
  getBirthPlaces,
  getCities,
  getCivilStatus,
  getCountries,
  getCountryStates,
  getOccupations,
  getRestrictedBirthPlaces,
  getTownships,
} from 'src/store/legalEntityCatalog/actions';
import { OCCUPATIONS_REQUEST } from 'src/store/legalEntityCatalog/actions/legalEntityRequestIdentifiers';
import { requestSearchPerson } from 'src/store/person/actions';
import { REQUEST_SEARCH_PERSON } from 'src/store/person/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';

// Constants
import { list as idTypes } from 'src/constants/idTypes';
import { profiles } from 'src/constants/profileTypes';

// functions
import {
  findNameByCode,
  mapCatatalogToDisplay as mapToDisplay,
} from 'src/functions/mapCatatalogToDisplay';
import { resolvesToNotPep } from 'src/functions/resolveToNotPep';

// Models
import EmissionDataFields, {
  EmissionBaseFields,
  LegalBeneficiary,
} from 'src/models/helperTypes/EmissionDataFields';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';

// Inputs
import { MultiFileLoader } from 'src/components/general';

// Methods
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import Tabs from 'src/components/general/CustomTabs';
import { cities as staticCities } from 'src/data/citiesCr.json';
import { states as staticStates } from 'src/data/statesCr.json';
import { townships as staticTownships } from 'src/data/townshipsCr.json';
import {
  addNaturalPersonACSEL,
  addNaturalPersonEquifax,
} from 'src/functions/equifax';
import { NaturalPersonEquifaxResponse } from 'src/models/responseTypes/NaturalPersonEquifaxResponse';
import processRequest from 'src/services/processRequest';
import { requestBanks } from 'src/store/creditorCatalog/actions';
import { makeAddressDataSection } from '../components/generationMethods';
import PersonValidationAlert from '../components/PersonValidationAlert';
import PersonValidationModal from '../components/PersonValidationModal';
import makeValidationSchema from './makeValidationSchema';
import {
  makeCollectiveGeneralDataSection,
  makeIndividualGeneralDataSection,
  makeOccupationDataSection,
} from './viewGenerationMethods';

// Components
import { minDesktopCardHeight } from '../useBaseWrapperStyles';
import { BeneficiaryDialog, CreditorCard, LegalHeirsCards } from './components';

// Hooks
import usePersonDataCommon from '../hooks/usePersonDataCommon';
import { usePersonPropertyValidator } from './hooks';

// subcomponents
import { IdCard } from '../../components';
import PersonValidationDIMEXAlert from '../components/PersonValidateDIMEXAlert/PersonValidateDIMEXAlert';
import PepCard from '../PepCard';

export const NaturalPersonData = ({
  onSubmit,
  dataFields,
  setDataFieldsAction,
  setNewDataFieldsAction,
  personData,
  quotation,
  requestUpdateEmission,
  resetRequestStatus,
  getCountriesAction,
  getOccupationsAction,
  countries,
  occupations,
  getCountryStatesAction,
  getCitiesAction,
  getTownshipsAction,
  countryStates,
  cities,
  townships,
  getBirthPlacesAction,
  getRestrictedBirthPlacesAction,
  birthPlaces,
  restrictedBirthPlaces,
  validatePersonAction,
  resetCedulaFilesAction,
  cedulaFileName,
  cedulaFileBase64,
  cedulaOptionalFileName,
  cedulaOptionalFileBase64,
  uploadCedulaAction,
  uploadCedulaOptionalAction,
  setLegalBeneficiariesAction,
  legalBeneficiaries,
  banks,
  requestBanksAction,
}: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [disabledCivilStatus, setDisabledCivilStatus] = useState(false);
  const [updateCivilStatus, setUpdateCivilStatus] = useState(false);
  const occupationsRequestStatus = useNetworkObserver(OCCUPATIONS_REQUEST);
  const requestingPerson = useNetworkObserver(REQUEST_SEARCH_PERSON);
  const [showValiationDIMEXAlert, setShowValiationDIMEXAlert] = useState(false);
  const [openBeneficiaries, setOpenBeneficiaries] = useState(false);
  const [showSaveBeneficiaries, setShowSaveBeneficiaries] = useState(false);

  const isCollectiveQuotation = useMemo(
    () => quotation.quotationProfile.type === profiles.COLLECTIVE,
    [quotation],
  );

  const occupationsDisplayItems = useMemo(
    () => mapToDisplay(occupations),
    [occupations],
  );

  // Agregated to manage origin occupation
  const [selectedOrigin, setSelectedOrigin] = useState<string>('');

  const isRequiredField = ['', '001', '003', '004'].includes(selectedOrigin);

  /**
   * Called when the form is submitted, and no errors occur
   */
  function handleFormSubmit(data: { [key: string]: FormValueTypes }): void {
    const baseData = data as EmissionBaseFields;

    if (!['001', '003', '004'].includes(selectedOrigin)) {
      baseData.enterprise = '';
      baseData.enterpriseActivity = '';
      baseData.occupation = '';
    }

    const staticState = staticStates.find(
      (item) => item.code === baseData.addressState,
    );
    const staticCitiesForState = staticCities.filter(
      (item) => item.parentId === staticState?.id,
    );
    const staticCity = staticCitiesForState.find(
      (item) => item.code === baseData.addressCity,
    );
    const staticTownshipsForCity = staticTownships.filter(
      (item) => item.parentId === staticCity?.id,
    );

    const emission: EmissionDataFields = {
      ...baseData,
      shareholders: [],
      addressCountryName:
        findNameByCode(countries, baseData.addressCountry) ||
        baseData.addressCountryName,
      addressStateName:
        findNameByCode(countryStates, baseData.addressState) ||
        baseData.addressStateName,
      addressCityName:
        findNameByCode(staticCitiesForState, baseData.addressCity) ||
        findNameByCode(cities, baseData.addressCity) ||
        dataFields.addressCityName,
      addressTownshipName:
        findNameByCode(staticTownshipsForCity, baseData.addressTownship) ||
        findNameByCode(townships, baseData.addressTownship) ||
        dataFields.addressTownshipName,
      birthPlaceName:
        findNameByCode(birthPlaces, baseData.birthPlace) ||
        baseData.birthPlaceName,
      otherNationalityName:
        findNameByCode(countries, baseData.otherNationality) ||
        baseData.otherNationalityName,
      civilStatusName:
        findNameByCode(civilStatus, baseData.civilStatus) ||
        baseData.civilStatusName,
      occupationName:
        findNameByCode(occupations, baseData.occupation) ||
        baseData.occupationName,
      profession:
        findNameByCode(occupations, baseData.occupation) || baseData.profession,
      originCountryName:
        findNameByCode(countries, baseData.originCountry) ||
        baseData.originCountryName,
      nationalityName:
        findNameByCode(birthPlaces, baseData.birthPlace) ||
        baseData.nationalityName,
      nationality: baseData.birthPlace,
      pep: false,
      bloodRelatedToPep: false,
      cicac: false,
      originDetail: baseData.enterprise,
    };

    setDataFieldsAction(emission);
    requestUpdateEmission(2);
  }

  /**
   * Note that the data managed by getInputProps mimics dataFields. To see
   * which values can be queried by getInputProps(inputName), check the
   * related data structure in EmissionSlice
   */
  const validationSchema = makeValidationSchema(
    !isCollectiveQuotation,
    quotation.summary.insuranceValue,
  );
  const { getInputProps, handleSubmit, dataSource, updateMultipleInputs } =
    useFormHook(
      dataFields as EmissionBaseFields,
      handleFormSubmit,
      validationSchema,
      {
        inputDependencies: {
          currentBank: ['creditorType'],
        },
        deferErrorCheck: true,
      },
    );
  /**
   * Values extracted from getInputProps, to facilitate data manipulation
   */
  const idNumber = getInputProps('idNumber').value;
  const identificationType = getInputProps('idType').value as string;
  const identificationValue = getInputProps('idNumber').value as string;
  const birthPlaceValue = getInputProps('birthPlace').value as string;

  const pep = getInputProps('pep').value;
  const bloodRelatedToPep = getInputProps('bloodRelatedToPep').value;
  const cicac = getInputProps('cicac').value;

  const isNotPep = useMemo(
    () =>
      resolvesToNotPep({
        pep: pep as boolean,
        bloodRelatedToPep: bloodRelatedToPep as boolean,
        pepLawArticle: false,
        cicac: cicac as boolean,
      }) || isCollectiveQuotation,
    [pep, bloodRelatedToPep, cicac, isCollectiveQuotation],
  );

  /**
   * The person property validation hook.
   */
  const {
    personIsInvalid,
    messageLabel,
    formatError,
    idCheckRequested,
    formatDimexError,
  } = usePersonPropertyValidator({
    personData,
    identificationNumber: identificationValue,
    identificationType,
    personSearchRequestStatus: requestingPerson,
    onPersonSearchRequested: validatePersonAction,
  });

  const {
    addressInformation: {
      countryOptions,
      stateOptions,
      requestingCountries,
      requestingStates,
      fetchCities,
      fetchTownships,
    },
    civilInformation: {
      birthPlaceOptions,
      civilStatusOptions,
      requestingBirthPlaces,
      requestingCivilStatus,
    },
    personIdData: {
      onLocalImageCaptured,
      disabled,
      failUpload,
      files,
      isFileUploaded,
    },
    classes,
    isMobile,
    wrapperClasses,
    requestingUpdateDataStatus,
    cedulaFileStatus,
    cedulaOptionalFileStatus,
    today,
    twoMonthsAhead,
    openValidationModal,
    setOpenValidationModal,
    openRestrictedCountryModal,
    setOpenRestrictedCountryModal,
    showValiationAlert,
    setShowValiationAlert,
    cityOptions,
    setCityOptions,
    townshipOptions,
    setTownshipOptions,
  } = usePersonDataCommon({
    dataFields,
    personData,
    isCollectiveQuotation,
    countries,
    countryStates,
    cities,
    townships,
    birthPlaces,
    civilStatus,
    restrictedBirthPlaces,
    getCountriesAction,
    getOccupationsAction,
    getCountryStatesAction,
    getBirthPlacesAction,
    getRestrictedBirthPlacesAction,
    getCitiesAction,
    getTownshipsAction,
    setDataFieldsAction,
    onSubmit,
    resetRequestStatus,
    birthPlaceValue,
    personIsInvalid,
    cedulaFileName,
    cedulaFileBase64,
    cedulaOptionalFileName,
    cedulaOptionalFileBase64,
    uploadCedulaAction,
    uploadCedulaOptionalAction,
    resetCedulaFilesAction,
    getInputProps,
    setSelectedOrigin,
  });

  useEffect(() => {
    setShowValiationDIMEXAlert(formatDimexError);
  }, [formatDimexError]);

  function buttonIsDisabled(): boolean {
    return (
      restrictedBirthPlaces.some((place) => place.code === birthPlaceValue) ||
      !isFileUploaded ||
      idNumber === '' ||
      !isNotPep ||
      personIsInvalid ||
      !idCheckRequested ||
      [cedulaFileStatus, cedulaOptionalFileStatus, requestingPerson].includes(
        'requesting',
      ) ||
      ['requesting', 'success'].includes(requestingUpdateDataStatus)
    );
  }

  const resetBeneficiaries = (): void => {
    setLegalBeneficiariesAction([]);
    setShowSaveBeneficiaries(false);
  };

  const openBeneficiariesDialog = (): void => {
    setOpenBeneficiaries(true);
  };

  const hideBeneficiariesAlert = (): void => {
    setShowSaveBeneficiaries(false);
  };

  const handleCloseBeneficiariesDialog = (): void => {
    setOpenBeneficiaries(false);
    setShowSaveBeneficiaries(false);
    const currentBeneficiaries: LegalBeneficiary[] = legalBeneficiaries || [];
    if (currentBeneficiaries.length === 0) {
      getInputProps('enableInheritor').onChange(false);
    }
  };

  const handleSubmitBeneficiaries = (): void => {
    setOpenBeneficiaries(false);
    setShowSaveBeneficiaries(true);
  };

  useEffect(() => {
    if (banks.length === 0) {
      requestBanksAction();
    }
  }, [banks.length, requestBanksAction]);

  const handleSelectCurrentTab = (newTab: number): void => {
    setCurrentPage(newTab);
  };

  const beneficiaryPage = (
    <LegalHeirsCards
      data-testid="assign-beneficiaries"
      getInputProps={getInputProps}
      onSetBeneficiaries={openBeneficiariesDialog}
      onSetLegalHeirs={resetBeneficiaries}
      showSaveAlert={showSaveBeneficiaries}
      onCloseSaveAlert={hideBeneficiariesAlert}
    />
  );

  const creditorsPage = (
    <CreditorCard
      isDisabled={false}
      data-testid="creditor-card"
      bankList={banks}
      dataSource={dataSource}
      getInputProps={getInputProps}
      currencySelected={quotation.quotationProfile.typeOfMoney}
      insuranceValue={quotation.summary.insuranceValue}
    />
  );

  const pages = [beneficiaryPage, creditorsPage];

  const getCreditorsTabs = (): string[] => {
    return ['Beneficiarios', 'Acreedores'];
  };

  const naturalPersonData = useCallback(async () => {
    setIsLoading(true);
    const person = await processRequest<
      NetworkResponse<NaturalPersonEquifaxResponse>
    >({
      method: 'GET',
      url: `/Person/personinformation?ruc=${getInputProps('idNumber').value}`,
    });
    await addNaturalPersonEquifax({
      person,
      dataFields,
      getInputProps,
      setNewDataFieldsAction,
      setCityOptions,
      setTownshipOptions,
      updateMultipleInputs,
    });
    setUpdateCivilStatus(true);
  }, []);

  useEffect(() => {
    if (
      requestingPerson === 'success' &&
      personData.isValidPerson &&
      !personData.personInnova?.thirdRuc &&
      identificationType === 'N'
    ) {
      naturalPersonData();
    }

    if (personData.personInnova?.thirdRuc) {
      addNaturalPersonACSEL({
        person: personData.personInnova,
        dataFields,
        getInputProps,
        setNewDataFieldsAction,
        setCityOptions,
        setTownshipOptions,
        updateMultipleInputs,
      });
      setUpdateCivilStatus(true);
    }
  }, [requestingPerson]);

  useEffect(() => {
    if (updateCivilStatus) {
      setDisabledCivilStatus(!getInputProps('civilStatus').value);
      setUpdateCivilStatus(false);
      setIsLoading(false);
    }
  }, [updateCivilStatus]);

  useEffect(() => {
    if (!personData.isValidPerson || identificationType !== 'N') {
      setIsLoading(false);
    }
  }, [personData]);

  return (
    <>
      <Grid container justifyContent="center">
        <PersonValidationModal
          data-testid="person-validation-modal"
          onClose={(): void => setOpenValidationModal(false)}
          open={openValidationModal}
        />
        <PersonValidationModal
          data-testid="restricted-country-validation-modal"
          onClose={(): void => setOpenRestrictedCountryModal(false)}
          open={openRestrictedCountryModal}
          message="Hemos validado la información brindada y no podemos continuar con la emisión de la póliza por este medio."
        />
        <Grid
          container
          spacing={2}
          item
          xs={12}
          md={7}
          className={wrapperClasses.pageContainer}
        >
          <Grid item xs={12}>
            <PersonValidationAlert
              data-testid="person-validation-alert"
              onCloseAlert={(): void => setShowValiationAlert(false)}
              showAlert={showValiationAlert}
            />
          </Grid>
          <Grid item xs={12}>
            <PersonValidationDIMEXAlert
              data-testid="person-validation-dimex-alert"
              onCloseValidationDimexAlert={(): void =>
                setShowValiationDIMEXAlert(false)
              }
              showValidationDimexAlert={showValiationDIMEXAlert}
            />
          </Grid>
          <Grid item xs={12}>
            <IdCard
              dropdownOptions={{
                key: 'idType',
                types: idTypes,
              }}
              inputOptions={{
                key: 'idNumber',
                label: 'Número de identificación',
                errorMessage: formatError,
                personIsInvalid,
                requestingPerson,
              }}
              instructionOptions={{
                inputId: 'naturalpersondata',
                headerTitle: '¿Cómo ingresar tu cédula?',
                statusMessage: messageLabel,
                contentTitle: 'Número de cédula',
                content: (
                  <img
                    id="sales-web-inspection-image-naturalpersondatainstructioncontent"
                    alt="cedula"
                    src="/images/id_format_guide.svg"
                  />
                ),
                instructions: (
                  <Typography
                    id="sales-web-inspection-label-naturalpersondatainstructiontext"
                    variant="body2"
                    align="center"
                  >
                    Escribe tu número de cédula, como aparece en ella, sin
                    espacios ni guiones.
                  </Typography>
                ),
              }}
              getInputProps={getInputProps}
            />
          </Grid>
          {isCollectiveQuotation
            ? makeCollectiveGeneralDataSection(personIsInvalid, getInputProps)
            : makeIndividualGeneralDataSection({
                isLoading: isLoading || requestingPerson === 'requesting',
                personIsInvalid,
                getInputProps,
                birthPlaceOptions,
                civilStatusOptions,
                requestingBirthPlaces,
                requestingCivilStatus,
                countryOptions,
                requestingCountries,
                disabledCivilStatus,
              })}
          {makeAddressDataSection({
            getInputProps,
            updateMultipleInputs,
            countryOptions,
            cityOptions,
            stateOptions,
            townshipOptions,
            requestingCountries,
            requestingStates,
            cardTitle: 'Dirección del contratante',
            detailLabel: 'Dirección detallada',
            personIsInvalid,
            isLoading: isLoading || requestingPerson === 'requesting',
            setCityOptions,
            setTownshipOptions,
            fetchCities,
            fetchTownships,
          })}
          {isCollectiveQuotation ? (
            <>
              <Grid item xs={12} md={12}>
                <DefaultCard
                  data-testid="insurance-start-day"
                  title="¿Cuándo empieza la protección de tu seguro?"
                  forcedMinHeight={minDesktopCardHeight + 10}
                >
                  <Grid item xs={12} md={6}>
                    <DateField
                      inputId="sales-web-inspection-input-effectiveDate"
                      data-testid="effectiveDate"
                      inputName="effectiveDate"
                      inputLabel="Fecha de inicio vigencia"
                      minDate={twoMonthsAhead}
                      maxDate={today}
                      dataSource={dataSource<string>()}
                      fallbackValue={undefined}
                      autoConstraint
                      isDisabled={
                        (getInputProps('assignDateSignature')
                          .value as boolean) || personIsInvalid
                      }
                    />
                  </Grid>

                  <CheckBox
                    inputId="sales-web-inspection-checkbox-assignDateSignature"
                    data-testid="onChangeAssignDateSignature"
                    checkTitle={
                      <span
                        id="sales-web-inspection-label-assignDateSignature"
                        style={{ fontSize: 15, fontWeight: 700 }}
                      >
                        Asignar la fecha conforme a la firma de documentos con
                        cliente
                      </span>
                    }
                    checkName="assignDateSignature"
                    defaultValue={
                      getInputProps('assignDateSignature').value as boolean
                    }
                    onChange={(val): void => {
                      getInputProps('assignDateSignature').onChange(val);
                      getInputProps('effectiveDate').onChange(
                        val ? format(today, 'dd/MM/yyyy') : '',
                      );
                    }}
                    disabled={personIsInvalid}
                    style={{ alignItems: 'center', paddingTop: 15 }}
                  />
                </DefaultCard>
              </Grid>
            </>
          ) : (
            <>
              {makeOccupationDataSection({
                getInputProps,
                countryOptions,
                isMobile,
                occupationOptions: occupationsDisplayItems,
                requestingCountries,
                requestingOccupations:
                  occupationsRequestStatus === 'requesting',
                personIsInvalid,
                setSelectedOrigin,
                isRequiredField,
                isLoading: isLoading || requestingPerson === 'requesting',
              })}
              <Grid item xs={12}>
                <MultiFileLoader
                  onDeleteFile={resetCedulaFilesAction}
                  onLocalImageCaptured={onLocalImageCaptured}
                  title="Foto del documento de identidad"
                  subTitle="Si tu identificación es cédula o DIMEX por favor sube una fotografía de ambos lados."
                  confirmationMessage="¿Estás seguro que deseas eliminar el documento?"
                  files={files}
                  maxFiles={2}
                  disabled={disabled}
                  deleteButtonId="delete-id-cedula"
                  id="test-cedula-component"
                  failUpload={failUpload}
                  data-testid="additional-documents-card"
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <PepCard
                  data-testid="pep-card"
                  getInputProps={getInputProps}
                  isDisabled={personIsInvalid}
                  isMobile={isMobile}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={12}>
            <DefaultCard data-testid="Other-information-cards">
              <Grid container alignItems="center">
                {isCollectiveQuotation && beneficiaryPage}
                {!isCollectiveQuotation && (
                  <>
                    <Grid item xs={12}>
                      <Box display="flex" sx={{ width: '100%' }}>
                        <Box>
                          <span className={classes.title}>
                            Información adicional
                          </span>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: 10 }}>
                      <Tabs
                        data-testid="tabs"
                        tabs={getCreditorsTabs()}
                        currentTab={currentPage}
                        onTabChange={handleSelectCurrentTab}
                      />
                      <PaddedContent
                        style={{ width: '100%', padding: 0, paddingTop: 10 }}
                      >
                        {pages[currentPage]}
                      </PaddedContent>
                    </Grid>
                  </>
                )}
              </Grid>
            </DefaultCard>
          </Grid>

          <Grid item xs={12} container justifyContent="center">
            <div className={wrapperClasses.buttonContainer}>
              <DefaultButton
                id="sales-web-inspection-button-next"
                data-testid="submit-button"
                onClick={handleSubmit}
                fullWidth
                disabled={buttonIsDisabled()}
              >
                {requestingUpdateDataStatus === 'requesting' ? (
                  <CircularProgress data-testid="spinner-btn" color="inherit" />
                ) : (
                  'Siguiente'
                )}
              </DefaultButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
      <BeneficiaryDialog
        data-testid="beneficiary-dialog"
        open={openBeneficiaries}
        onCloseDialog={handleCloseBeneficiariesDialog}
        onSubmitBeneficiaries={handleSubmitBeneficiaries}
      />
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  cedulaFileName: string;
  cedulaFileBase64: string;
  cedulaOptionalFileName: string;
  cedulaOptionalFileBase64: string;
  dataFields: EmissionDataFields;
  personData: PersonDataResponse;
  quotation: Quotation;
  occupations: GeneralCatalogItem[];
  countries: GeneralCatalogItem[];
  cities: GeneralCatalogItem[];
  countryStates: GeneralCatalogItem[];
  townships: GeneralCatalogItem[];
  birthPlaces: GeneralCatalogItem[];
  restrictedBirthPlaces: GeneralCatalogItem[];
  civilStatus: GeneralCatalogItem[];
  legalBeneficiaries?: LegalBeneficiary[];
  banks: GeneralCatalogItem[];
} => ({
  cedulaFileName: state.emissionPolicy.identificationFileName,
  cedulaFileBase64: state.emissionPolicy.identificationBase64,
  cedulaOptionalFileName: state.emissionPolicy.identificationOptionalFileName,
  cedulaOptionalFileBase64: state.emissionPolicy.identificationOptionalBase64,
  dataFields: state.emissionPolicy.dataFields,
  personData: state.person.personData,
  quotation: state.quotation.quotation,
  occupations: state.legalEntityCatalog.occupations,
  countries: state.legalEntityCatalog.countries,
  cities: state.legalEntityCatalog.cities,
  countryStates: state.legalEntityCatalog.countryStates,
  townships: state.legalEntityCatalog.townships,
  birthPlaces: state.legalEntityCatalog.birthPlaces,
  restrictedBirthPlaces: state.legalEntityCatalog.restrictedBirthPlaces,
  civilStatus: state.legalEntityCatalog.civilStatus,
  legalBeneficiaries: state.emissionPolicy.legalBeneficiaries,
  banks: state.creditorCatalog.banks,
});

const reduxActions = {
  setDataFieldsAction: setDataFields,
  setNewDataFieldsAction: setNewDataFields,
  requestUpdateEmission: updateEmissionByPage,
  resetRequestStatus: setRequestStatus,
  getOccupationsAction: getOccupations,
  getCountriesAction: getCountries,
  getCountryStatesAction: getCountryStates,
  getCitiesAction: getCities,
  getTownshipsAction: getTownships,
  getBirthPlacesAction: getBirthPlaces,
  getRestrictedBirthPlacesAction: getRestrictedBirthPlaces,
  getCivilStatusAction: getCivilStatus,
  validatePersonAction: requestSearchPerson,
  uploadCedulaAction: setIdentificationBase64,
  uploadCedulaOptionalAction: setIdentificationOptionalBase64,
  resetCedulaFilesAction: resetCedulaFiles,
  setLegalBeneficiariesAction: setLegalBeneficiaries,
  requestBanksAction: requestBanks,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(NaturalPersonData);
